import { useEffect, useRef, useState, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { API_URL, getAuthHeaders } from '../services/api';
import Terminal from './Terminal';

interface LogEntry {
	stream: string;
	line: string;
	timestamp: number;
}

interface LogViewerProps {
	agentId: string;
	commandId: string | null;
	isActive: boolean;
	onClose?: () => void;
	/** Called when the log stream finishes (command completed) */
	onComplete?: () => void;
	/** 'log' = plain text log viewer (default), 'terminal' = interactive xterm.js */
	mode?: 'log' | 'terminal';
}

export default function LogViewer({ agentId, commandId, isActive, onClose, onComplete, mode = 'log' }: LogViewerProps) {
	// In terminal mode, delegate entirely to the Terminal component
	if (mode === 'terminal') {
		return (
			<Terminal
				agentId={agentId}
				commandId={commandId}
				isActive={isActive}
				onClose={onClose}
			/>
		);
	}

	const { firebaseUser } = useAuth();
	const [logs, setLogs] = useState<LogEntry[]>([]);
	const [isConnected, setIsConnected] = useState(false);
	const [isDone, setIsDone] = useState(false);
	const logsEndRef = useRef<HTMLDivElement>(null);
	const abortRef = useRef<AbortController | null>(null);
	const [stdinValue, setStdinValue] = useState('');
	const [isSendingInput, setIsSendingInput] = useState(false);
	const [stdinError, setStdinError] = useState<string | null>(null);

	// Auto-scroll to bottom when new logs arrive
	useEffect(() => {
		logsEndRef.current?.scrollIntoView({ behavior: 'smooth' });
	}, [logs]);

	// Reset logs when commandId changes
	const prevCommandId = useRef<string | null>(null);
	useEffect(() => {
		if (commandId !== prevCommandId.current) {
			setLogs([]);
			setIsDone(false);
			prevCommandId.current = commandId;
		}
	}, [commandId]);

	// Connect to SSE stream using fetch (supports Authorization header)
	const connectToStream = useCallback(async () => {
		if (!commandId || !isActive || !firebaseUser) return;

		// Abort any existing connection
		if (abortRef.current) {
			abortRef.current.abort();
		}

		const controller = new AbortController();
		abortRef.current = controller;

		try {
			const idToken = await firebaseUser.getIdToken();
			const url = `${API_URL}/api/v1/agents/${agentId}/logs/${commandId}`;

			const response = await fetch(url, {
				headers: {
					...getAuthHeaders(idToken),
					'Accept': 'text/event-stream',
				},
				signal: controller.signal,
			});

			if (!response.ok) {
				console.error('[LogViewer] Failed to connect:', response.status);
				setIsConnected(false);
				return;
			}

			setIsConnected(true);
			const reader = response.body?.getReader();
			if (!reader) return;

			const decoder = new TextDecoder();
			let buffer = '';

			while (true) {
				const { done, value } = await reader.read();
				if (done) {
					setIsConnected(false);
					setIsDone(true);
					onComplete?.();
					break;
				}

				buffer += decoder.decode(value, { stream: true });

				// Parse SSE lines from buffer
				const lines = buffer.split('\n');
				buffer = lines.pop() || '';

				for (const line of lines) {
					if (line.startsWith('data: ')) {
						try {
							const logEntry: LogEntry = JSON.parse(line.slice(6));
							setLogs(prev => [...prev, logEntry]);
						} catch {
							// Ignore unparseable lines (e.g. SSE comments)
						}
					}
				}
			}
		} catch (err: any) {
			if (err.name !== 'AbortError') {
				console.error('[LogViewer] Stream error:', err);
			}
			setIsConnected(false);
		}
	}, [commandId, agentId, firebaseUser, isActive, onComplete]);

	useEffect(() => {
		connectToStream();

		return () => {
			if (abortRef.current) {
				abortRef.current.abort();
				abortRef.current = null;
				setIsConnected(false);
			}
		};
	}, [connectToStream]);

	const sendStdin = useCallback(async () => {
		if (!firebaseUser || !commandId || !stdinValue.trim()) return;
		setStdinError(null);
		setIsSendingInput(true);
		try {
			const idToken = await firebaseUser.getIdToken();
			const url = `${API_URL}/api/v1/agents/${agentId}/logs/${commandId}/stdin`;
			const response = await fetch(url, {
				method: 'POST',
				headers: getAuthHeaders(idToken),
				body: JSON.stringify({ line: stdinValue }),
			});
			if (!response.ok) {
				const text = await response.text();
				throw new Error(text || 'Failed to send input');
			}
			setStdinValue('');
		} catch (err: any) {
			setStdinError(err?.message || 'Failed to send input');
		} finally {
			setIsSendingInput(false);
		}
	}, [agentId, commandId, firebaseUser, stdinValue]);

	const handleInputKey = useCallback((event: React.KeyboardEvent<HTMLInputElement>) => {
		if (event.key === 'Enter' && !event.shiftKey) {
			event.preventDefault();
			sendStdin();
		}
	}, [sendStdin]);

	if (!isActive) {
		return null;
	}

	return (
		<div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl overflow-hidden">
			{/* Header */}
			<div className="px-4 py-3 bg-slate-100 dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700 flex items-center justify-between">
				<div className="flex items-center space-x-3">
					<div className="flex items-center space-x-2">
						<div className={`w-2 h-2 rounded-full ${isConnected ? 'bg-green-500 animate-pulse' : isDone ? 'bg-blue-400' : 'bg-slate-400'}`} />
						<span className="text-sm font-medium text-slate-700 dark:text-slate-300">
							{isConnected ? 'Live Logs' : isDone ? 'Complete' : 'Connecting...'}
						</span>
					</div>
					{commandId && (
						<span className="text-xs text-slate-500 dark:text-slate-400 font-mono">
							{commandId.slice(0, 8)}
						</span>
					)}
				</div>
				{onClose && (
					<button
						onClick={onClose}
						className="p-1 hover:bg-slate-200 dark:hover:bg-slate-700 rounded transition-colors"
					>
						<svg className="w-5 h-5 text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
							<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
						</svg>
					</button>
				)}
			</div>

			{/* Log Content */}
			<div className="bg-slate-950 text-green-400 font-mono text-sm h-64 overflow-y-auto p-4">
				{logs.length === 0 ? (
					<div className="text-slate-500 text-center py-8">
						Waiting for logs...
					</div>
				) : (
					<div className="space-y-0.5">
						{logs.map((log, i) => (
							<div key={i} className="flex items-start space-x-2">
								<span className="text-slate-600 text-xs select-none">
									{new Date(log.timestamp).toLocaleTimeString()}
								</span>
								<span className={`text-xs select-none ${log.stream === 'stderr' ? 'text-red-400' : 'text-blue-400'}`}>
									[{log.stream}]
								</span>
								<span className={log.stream === 'stderr' ? 'text-red-300' : 'text-green-300'}>
									{log.line}
								</span>
							</div>
						))}
						<div ref={logsEndRef} />
					</div>
				)}
			</div>

			{/* Footer */}
			<div className="px-4 py-2 bg-slate-100 dark:bg-slate-800 border-t border-slate-200 dark:border-slate-700 space-y-2">
				<div className="flex items-center justify-between">
					<span className="text-xs text-slate-500 dark:text-slate-400">
						{logs.length} line{logs.length !== 1 ? 's' : ''}
					</span>
					<button
						onClick={() => setLogs([])}
						className="text-xs text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-200 transition-colors"
					>
						Clear
					</button>
				</div>
				{commandId && !isDone && (
					<div className="space-y-1">
						<label className="text-xs text-slate-500 dark:text-slate-400">Send stdin to installer</label>
						<div className="flex items-center space-x-2">
							<input
								type="text"
								value={stdinValue}
								onChange={(e) => setStdinValue(e.target.value)}
								onKeyDown={handleInputKey}
								placeholder="Type installer response..."
								className="flex-1 px-3 py-1.5 rounded border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 text-sm text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-indigo-500"
							/>
							<button
								onClick={sendStdin}
								disabled={!stdinValue.trim() || isSendingInput}
								className={`px-3 py-1.5 rounded text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors`}
							>
								{isSendingInput ? 'Sending...' : 'Send'}
							</button>
						</div>
						{stdinError && (
							<p className="text-xs text-red-400">{stdinError}</p>
						)}
					</div>
				)}
			</div>
		</div>
	);
}
